// Flusia self-tracking ads — registrazione e preciso calendario demo commerciale.
// Iubenda Consent Mode V2 (default denied) + GTM + gtag Google Ads + Meta Pixel.
// Gli eventi funnel esistenti (onboardingEvents.ts → dataLayer) vengono raccolti
// da GTM una volta caricato: asset, non conflitto.
//
// Meta Pixel NON viene iniettato da loadMarketingTags(): il Pixel ignora il
// Google Consent Mode (a differenza di gtag), quindi fbq('init')+PageView
// partirebbero anche a consenso zero. loadMetaPixel() viene chiamato SOLO dal
// bridge di consenso Iubenda quando il purpose 5 (marketing) è granted.
import {
  updateConsentFromIubendaPreference,
  restoreClickIdsInUrl,
  collectAttributionData,
} from '../attribution/attributionCookie';
import { trackGa4Event } from './onboardingEvents';
import { dispatchConversionOnce, isInternalMarketingTest } from './selfTrackingPolicy';

const GTM_ID = 'GTM-T5TFST6D';
const GOOGLE_ADS_ID = 'AW-17967284531';
// ATTENZIONE (25/08/2026): G-1N2DXHEPMP NON e' la property di flusia.it. E' la
// configurazione del tag Google "digital-crm.it", il vecchio progetto. Per questo
// dal 2024 nessun evento personalizzato e' mai arrivato nella property Flusia
// (flusia-aaf65, 518284746): ci arrivavano solo gli eventi automatici, via
// destinazione collegata. Lo stream web di flusia.it e' G-PPR3BY1VLT.
// I 6 tag evento del container GTM-T5TFST6D sono stati corretti (versione 6).
// Usare lo stream Flusia anche per gli eventi inviati direttamente con gtag.
const GA4_ID = 'G-PPR3BY1VLT';
const REGISTRATION_CONVERSION_LABEL = '9ogCCOzbh9IcELOCvPdC';
// Etichetta dell'azione "Prenotazione appuntamento" (attiva e primaria
// nell'account 7488431648). Fino al 25/08/2026 nessuno la sparava: il tag GTM
// che ci provava scattava su OGNI cta_click ed e' stato messo in pausa, quindi
// gli appuntamenti veri non venivano contati da nessuna parte.
const BOOKING_CONVERSION_LABEL = 'azifCJCgu_8bELOCvPdC';
const IUBENDA_SITE_ID = 48163659;
const META_PIXEL_ID = import.meta.env.VITE_META_FLUSIA_PIXEL_ID || '892063210090142';

const CLARITY_PROJECT_ID = 'xupydhha0k';
/**
 * Ambienti di prova (dev.flusia.it, MD/architecture/staging-dev-flusia.md): nessun tag pubblicitario,
 * nessuna conversione, nessuna registrazione Clarity. In produzione la variabile non c'e'.
 */
const TAGS_DISABLED = import.meta.env.VITE_MARKETING_TAGS_DISABLED === 'true';

let loaded = false;
let pixelLoaded = false;
let clarityLoaded = false;

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _iub?: unknown[] & { csConfiguration?: Record<string, unknown> };
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Array<Record<string, unknown>>;
    __flusiaConsentQueue?: unknown[];
    __flusiaOnConsent?: (pref: unknown) => void;
  }
}

function injectScript(src: string, id: string): void {
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id; s.async = true; s.src = src;
  document.head.appendChild(s);
}

function injectInline(code: string, id: string): void {
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id; s.text = code;
  document.head.appendChild(s);
}

export function loadMarketingTags(): void {
  if (TAGS_DISABLED || loaded || typeof document === 'undefined') return;
  loaded = true;

  // 1) Consent Mode V2 default denied (stessi valori del sito)
  injectInline(`
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = window.gtag || gtag;
    gtag('consent', 'default', {
      'ad_storage': 'denied', 'ad_user_data': 'denied', 'ad_personalization': 'denied',
      'analytics_storage': 'denied', 'functionality_storage': 'denied',
      'personalization_storage': 'denied', 'security_storage': 'granted',
      'wait_for_update': 500, 'region': ['IT', 'EU']
    });
    // Con ad_storage negato (o cookie tagliati da Safari/Firefox ITP) il cookie
    // _gcl_aw non e' leggibile: senza questo la conversione di registrazione parte
    // priva dell'identificativo di click e Google non la attribuisce mai.
    gtag('set', 'url_passthrough', true);
  `, 'flusia-consent-default');

  // 2) Iubenda (stesso siteId del sito: licenza Ultra copre i sottodomini)
  //    callback onPreferenceExpressed → bridge di consenso (vedi sotto): può
  //    sparare subito se l'utente ha già una preferenza salvata (return visitor).
  injectInline(`
    var _iub = _iub || [];
    _iub.csConfiguration = {
      "siteId": ${IUBENDA_SITE_ID}, "cookiePolicyId": ${IUBENDA_SITE_ID},
      "lang": "it", "storage": { "useSiteId": true }, "googleConsentMode": "template",
      "callback": {
        "onPreferenceExpressed": function (preference) {
          window.__flusiaConsentQueue = window.__flusiaConsentQueue || [];
          if (window.__flusiaOnConsent) { window.__flusiaOnConsent(preference); }
          else { window.__flusiaConsentQueue.push(preference); }
        }
      }
    };
  `, 'flusia-iubenda-config');
  injectScript(`https://cs.iubenda.com/autoblocking/${IUBENDA_SITE_ID}.js`, 'flusia-iubenda-autoblock');
  injectScript('https://cdn.iubenda.com/cs/iubenda_cs.js', 'flusia-iubenda-cs');

  // 2bis) Bridge di consenso: aggiorna il cookie attribution "ultimo noto" e,
  // solo se il marketing (purpose 5) è granted, carica il Meta Pixel.
  window.__flusiaOnConsent = handleIubendaPreference;
  const queuedConsent = window.__flusiaConsentQueue ?? [];
  queuedConsent.forEach(handleIubendaPreference);
  window.__flusiaConsentQueue = [];

  // 3) gtag Google Ads + GA4 (conversione registrazione + evento sign_up).
  //    DEVE precedere GTM: il container GTM configura la stessa property GA4
  //    e se e' lui il primo a reclamare la destinazione G-, l'istanza che crea
  //    IGNORA i comandi gtag() diretti (verificato il 23/08 sia su localhost
  //    sia sul sito live: eventi accettati, session_id vivo, zero hit di rete).
  //    Caricando prima il nostro config, la destinazione ascolta gtag() e GTM
  //    si aggancia a quella esistente.
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`, 'flusia-gtag-js');
  injectInline(`
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = window.gtag || gtag;
    gtag('js', new Date());
    gtag('config', '${GOOGLE_ADS_ID}', { 'allow_enhanced_conversions': true });
    // Cross-domain flusia.it <-> app.flusia.it: il linker tiene insieme la
    // sessione GA4 di chi arriva dal sito e si registra sull'app.
    gtag('set', 'linker', { 'domains': ['flusia.it', 'app.flusia.it'] });
    gtag('config', '${GA4_ID}', { 'send_page_view': false });
  `, 'flusia-gtag-config');

  // 4) GTM (raccoglie gli eventi flusia.* già pushati da onboardingEvents.ts)
  injectInline(`
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${GTM_ID}');
  `, 'flusia-gtm');

}

/** Meta Pixel base code — chiamato SOLO dal bridge di consenso (purpose 5
 *  marketing granted), mai da loadMarketingTags(): il Pixel ignora il Google
 *  Consent Mode e sparerebbe init+PageView anche a consenso zero. */
function loadMetaPixel(): void {
  if (TAGS_DISABLED || pixelLoaded || typeof document === 'undefined') return;
  pixelLoaded = true;
  injectInline(`
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${META_PIXEL_ID}');
    fbq('track', 'PageView');
  `, 'flusia-meta-pixel');
}

/** Microsoft Clarity (session replay del funnel /register, stesso progetto del
 *  sito flusia.it) — caricato SOLO se la misurazione (purpose 4) è granted.
 *  Servono ENTRAMBI: lo snippet ufficiale con lo stub PRIMA del tag (senza,
 *  il tag non inizializza window.clarity) e la chiamata consentv2 (dal
 *  31/10/2025 Clarity scarta le sessioni EEA senza segnale di consenso). */
function loadClarity(marketingGranted: boolean): void {
  if (TAGS_DISABLED || typeof document === 'undefined') return;
  if (!clarityLoaded) {
    clarityLoaded = true;
    injectInline(`
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window,document,"clarity","script","${CLARITY_PROJECT_ID}");
    `, 'flusia-clarity');
  }
  (window as unknown as { clarity: (...args: unknown[]) => void }).clarity('consentv2', {
    ad_Storage: marketingGranted ? 'granted' : 'denied',
    analytics_Storage: 'granted',
  });
}

/** Handler del bridge di consenso Iubenda: aggiorna il cookie "ultimo noto"
 *  (attributionCookie) e inizializza il Pixel solo se il marketing è granted. */
function handleIubendaPreference(pref: unknown): void {
  updateConsentFromIubendaPreference(pref);
  const purposes = (pref as { purposes?: Record<string, boolean> } | null)?.purposes;
  if (purposes?.['5'] === true) {
    window.fbq?.('consent', 'grant');
    loadMetaPixel();
  } else if (purposes) {
    window.fbq?.('consent', 'revoke');
  }
  if (purposes?.['4'] === true) {
    loadClarity(purposes?.['5'] === true);
  } else if (purposes) {
    (window as unknown as { clarity?: (...args: unknown[]) => void }).clarity?.('consentv2', {
      ad_Storage: 'denied', analytics_Storage: 'denied',
    });
  }
}

/** Conversione client-side alla registrazione completata (dedup CAPI via eventID).
 *  `email` alimenta le Enhanced Conversions for Leads: gtag la normalizza e ne
 *  invia solo l'hash SHA-256, e la scarta da solo se ad_user_data è denied
 *  (Consent Mode V2) — nessun dato in chiaro lascia il browser. */
export function fireRegistrationConversion(eventId: string | null, email?: string | null): void {
  if (TAGS_DISABLED || typeof window === 'undefined' || !eventId || isInternalMarketingTest(email)) return;
  dispatchConversionOnce(eventId, () => {
  // GA4 sign_up (evento raccomandato, parametro `method`): PRIMA di tutto il
  // resto, cosi' un errore nel ramo Ads (URL, gtag, pixel) non lo porta via.
  // VA NEL DATALAYER, non in gtag(): su questa property gli eventi gtag()
  // diretti non risultano nei report (verificato il 23-24/08 con Realtime
  // API), mentre il tag "GA4 - sign_up" del container GTM-T5TFST6D (trigger
  // sull'evento custom omonimo, ID misurazione G-PPR3BY1VLT) e' verificato
  // arrivare (25/08). Il tag GTM NON inoltra `method` finche' non gli si
  // aggiunge il parametro evento.
  trackGa4Event('sign_up', { method: 'email', event_id: eventId });
  // gtag legge l'identificativo di click dall'URL corrente (o dal cookie _gcl_aw,
  // che però non esiste se il consenso pubblicitario è negato). Dopo i passi
  // interni della registrazione la query string è persa: senza questo ripristino
  // la conversione parte anonima e Google la scarta. Va PRIMA della gtag().
  restoreClickIdsInUrl();
  const consent = collectAttributionData().consent;
  window.gtag?.('set', 'user_data', email && consent?.ad_user_data === 'granted' ? { email } : {});
  window.gtag?.('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${REGISTRATION_CONVERSION_LABEL}`,
    transaction_id: eventId,
  });
  if (window.fbq && consent?.ad_user_data === 'granted') {
    if (eventId) window.fbq('track', 'CompleteRegistration', {}, { eventID: eventId });
    else window.fbq('track', 'CompleteRegistration');
  }
  });
}

/** Solo dopo conferma server del calendario commerciale Flusia e del lead. */
export function fireBookingConversion(bookingId: string, email?: string | null): void {
  if (TAGS_DISABLED || typeof window === 'undefined' || !bookingId || isInternalMarketingTest(email)) return;
  const eventId = `demo:${bookingId}`;
  dispatchConversionOnce(eventId, () => {
  // Stessa ragione della registrazione: dopo i passi del calendario la query
  // string e' persa e senza questo la conversione partirebbe senza gclid.
  restoreClickIdsInUrl();
  const consent = collectAttributionData().consent;
  window.gtag?.('set', 'user_data', email && consent?.ad_user_data === 'granted' ? { email } : {});
  window.gtag?.('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${BOOKING_CONVERSION_LABEL}`,
    transaction_id: eventId,
  });
  // Destinazione esplicita: nessun tag GTM book_appointment è pubblicato.
  // Non duplicare questo invio con un secondo tag evento nello stesso container.
  window.gtag?.('event', 'book_appointment', {
    send_to: GA4_ID, method: 'booking_page', event_id: eventId,
  });
  if (consent?.ad_user_data === 'granted') window.fbq?.('track', 'Schedule', {}, { eventID: eventId });
  });
}
